import { evidenceLocatorKey } from '@/components/mobile-citations/citation-evidence';
import type { MobileUiConfigArtifact, MobileUiConfigResponse } from '@/shared/mobile-service/api/cors-mobile-api';
import type { MobileEvidence } from '@/shared/types';
import { humanize, resolveArtifactColumns, type ResolvedArtifactColumns } from './columns';
import { isFilterActive, type ColumnFilterValue, type FilterState } from './where';

/** Resolve viewer table columns when the citation pane can show a table. */
export function citationTableColumns(
  artifact: MobileUiConfigArtifact | undefined,
  uiConfig: MobileUiConfigResponse | undefined,
): ResolvedArtifactColumns | null {
  if (!artifact || !uiConfig) return null;
  const resolved = resolveArtifactColumns(artifact.graphql_type, uiConfig);
  if (!resolved || resolved.columns.length === 0) return null;
  return resolved;
}

function isEmptyColumnValue(value: unknown): boolean {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value as object).length === 0;
  return false;
}

/**
 * Columns to show initially. A field that is empty on every loaded row starts
 * unchecked; the caller keeps it in the selector so it can be turned back on.
 * When every field is empty, the first one stays so the grid is not blank.
 */
export function populatedColumnFields(
  fields: string[],
  rows: Record<string, unknown>[],
): string[] {
  const populated = fields.filter((field) =>
    rows.some((row) => !isEmptyColumnValue(row[field])),
  );
  if (populated.length > 0) return populated;
  return fields.slice(0, 1);
}

const AGGREGATE_ROLLUPS = ['count', 'min', 'max', 'sum', 'avg'] as const;

const AGGREGATE_WORDS: Record<(typeof AGGREGATE_ROLLUPS)[number], string> = {
  count: 'count',
  min: 'minimum',
  max: 'maximum',
  sum: 'sum',
  avg: 'average',
};

const COMPARATOR_KEYS = new Set([
  'equals',
  'contains',
  'startsWith',
  'endsWith',
  'gt',
  'gte',
  'lt',
  'lte',
  'in',
  'not',
  'notIn',
  'mode',
]);

const UTC_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type CitationQuerySummaryOptions = {
  /** Curated column headings. Missing fields fall back to a humanized name. */
  fieldLabels?: ReadonlyMap<string, string>;
  /**
   * Viewer config. Magnet queries use the artifact title (the Export.xml name)
   * instead of a humanized GraphQL field name.
   */
  uiConfig?: MobileUiConfigResponse;
};

/** ``count`` with no fields, otherwise ``minimum Created Date Time``. */
export function formatAggregateSelection(
  aggregates: unknown,
  fieldLabels?: ReadonlyMap<string, string>,
): string | null {
  if (!isPlainObject(aggregates)) return null;
  const parts: string[] = [];
  for (const rollup of AGGREGATE_ROLLUPS) {
    if (!(rollup in aggregates)) continue;
    const fields = aggregates[rollup];
    const word = AGGREGATE_WORDS[rollup];
    if (!Array.isArray(fields) || fields.length === 0) {
      parts.push(word);
      continue;
    }
    for (const field of fields) {
      if (typeof field === 'string' && field.trim()) {
        parts.push(`${word} ${plainFieldLabel(field.trim(), fieldLabels)}`);
      }
    }
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

/** One-line query identity plus plain-language filter and sort lines for the citation pane. */
export function citationQuerySummary(
  evidence: MobileEvidence | undefined,
  options?: CitationQuerySummaryOptions,
): string[] {
  if (!evidence) return [];
  const labels = options?.fieldLabels;
  const magnetTitles = magnetQueryTitles(options?.uiConfig);
  const lines: string[] = [];
  const root = describeQueryRoot(evidence.queryName, evidence.queryShape, magnetTitles);
  if (root) lines.push(root);
  const where = describeWhere(evidence.where, labels);
  if (where) lines.push(where);
  const aggregates = formatAggregateSelection(evidence.aggregates, labels);
  if (aggregates) lines.push(aggregates);
  const orderBy = describeOrderBy(evidence.orderBy, labels);
  if (orderBy) lines.push(orderBy);
  const groupedBy = describeGroupedBy(evidence.by, labels);
  if (groupedBy) lines.push(groupedBy);
  const having = describeHaving(evidence.having, labels);
  if (having) lines.push(having);
  if (typeof evidence.orderByCount === 'string' && evidence.orderByCount.trim()) {
    const direction = directionWord(evidence.orderByCount) ?? humanize(evidence.orderByCount);
    lines.push(`Sorted by count, ${direction}`);
  }
  const label = typeof evidence.label === 'string' ? evidence.label.trim() : '';
  if (label && lines[0]?.toLowerCase() !== label.toLowerCase()) {
    lines.unshift(label);
  }
  return lines;
}

/**
 * One summary per cited query. Artifact rows are not queries. Duplicate locators
 * collapse so the same result set is not listed twice.
 */
export function queryCitationSummaries(
  sources: Array<{ evidence?: MobileEvidence | null }> | undefined,
  uiConfig?: MobileUiConfigResponse,
): string[][] {
  if (!sources) return [];
  const seen = new Set<string>();
  const groups: string[][] = [];
  for (const source of sources) {
    const evidence = source.evidence;
    if (!evidence || (evidence.kind !== 'query' && evidence.kind !== 'absence')) continue;
    const key = evidenceLocatorKey(evidence);
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    const lines = citationQuerySummary(evidence, { uiConfig });
    if (lines.length > 0) groups.push(lines);
  }
  return groups;
}

/**
 * Magnet Export.xml names keyed by the list and singular root queries.
 *
 * The GraphQL name repeats the category path (``mediaMediaMedia…``). The
 * curated title is the unique XML name. Other vendors stay humanized.
 */
function magnetQueryTitles(
  uiConfig: MobileUiConfigResponse | undefined,
): ReadonlyMap<string, string> | undefined {
  if (!uiConfig) return undefined;
  const titles = new Map<string, string>();
  for (const artifact of Object.values(uiConfig.artifacts)) {
    if (artifact.endpoint !== 'magnet') continue;
    const title = artifact.title?.trim();
    if (!title) continue;
    for (const queryName of [
      artifact.front_end?.graphql_root_query,
      artifact.front_end?.graphql_root_query_singular,
    ]) {
      const name = queryName?.trim();
      if (name) titles.set(name, title);
    }
  }
  return titles.size > 0 ? titles : undefined;
}

function describeQueryRoot(
  queryName: unknown,
  queryShape: unknown,
  magnetTitles?: ReadonlyMap<string, string>,
): string | null {
  const raw = typeof queryName === 'string' ? queryName.trim() : '';
  const curated = raw ? magnetTitles?.get(raw) : undefined;
  const name = curated || (raw ? humanize(raw) : '');
  const shape = describeQueryShape(queryShape);
  if (name && shape) return `${name}, ${shape}`;
  return name || shape || null;
}

function describeQueryShape(queryShape: unknown): string | null {
  if (typeof queryShape !== 'string' || !queryShape.trim()) return null;
  if (queryShape === 'aggregate') return 'summary';
  if (queryShape === 'groupBy') return 'grouped';
  if (queryShape === 'count') return 'count';
  return humanize(queryShape);
}

function describeWhere(where: unknown, labels?: ReadonlyMap<string, string>): string | null {
  if (!isPlainObject(where) || Object.keys(where).length === 0) return null;
  return describeWhereNode(where, labels, false) ?? describeLoose(where, labels);
}

function describeWhereNode(
  node: unknown,
  labels: ReadonlyMap<string, string> | undefined,
  nested: boolean,
): string | null {
  if (!isPlainObject(node)) return null;
  if (isComparator(node)) return describeComparator(node);
  const parts: string[] = [];
  for (const [key, value] of Object.entries(node)) {
    if (key === 'mode') continue;
    if (key === 'AND' || key === 'OR' || key === 'NOT') {
      const logical = describeLogical(key, value, labels, nested);
      if (logical) parts.push(logical);
      continue;
    }
    const label = plainFieldLabel(key, labels);
    if (isPlainObject(value)) {
      if (isComparator(value)) {
        const phrase = describeComparator(value);
        if (phrase) parts.push(`${label} ${phrase}`);
        continue;
      }
      const inner = describeWhereNode(value, labels, true);
      if (inner) parts.push(`${label} ${inner}`);
      else parts.push(`${label} ${describeLoose(value, labels)}`);
      continue;
    }
    parts.push(`${label} ${formatOperand(value)}`);
  }
  if (parts.length === 0) return null;
  const joined = joinList(parts, 'and', { commaBeforePair: true });
  return nested && parts.length > 1 ? `(${joined})` : joined;
}

function describeLogical(
  key: 'AND' | 'OR' | 'NOT',
  value: unknown,
  labels: ReadonlyMap<string, string> | undefined,
  nested: boolean,
): string | null {
  if (key === 'NOT') {
    const inner = joinWhereItems(value, 'and', labels);
    return inner ? `not (${inner})` : null;
  }
  const conjunction = key === 'OR' ? 'or' : 'and';
  const joined = joinWhereItems(value, conjunction, labels);
  if (!joined) return null;
  const count = Array.isArray(value) ? value.length : 1;
  return nested && count > 1 ? `(${joined})` : joined;
}

function joinWhereItems(
  value: unknown,
  conjunction: 'and' | 'or',
  labels: ReadonlyMap<string, string> | undefined,
): string | null {
  const items = Array.isArray(value) ? value : [value];
  const parts = items
    .map((item) => (isPlainObject(item) ? describeWhereNode(item, labels, true) : formatOperand(item)))
    .filter((part): part is string => Boolean(part));
  if (parts.length === 0) return null;
  return joinList(parts, conjunction, { commaBeforePair: true });
}

function describeComparator(comparator: Record<string, unknown>): string | null {
  const keys = Object.keys(comparator).filter((key) => key !== 'mode');
  if (keys.length === 1 && keys[0] === 'not') return describeNot(comparator.not);
  const phrases: string[] = [];
  const lower = comparator.gte !== undefined ? comparator.gte : comparator.gt;
  const upper = comparator.lte !== undefined ? comparator.lte : comparator.lt;
  const hasLower = comparator.gte !== undefined || comparator.gt !== undefined;
  const hasUpper = comparator.lte !== undefined || comparator.lt !== undefined;
  if (hasLower && hasUpper) {
    phrases.push(`is between ${formatOperand(lower)} and ${formatOperand(upper)}`);
  } else {
    if (comparator.gt !== undefined) phrases.push(`is greater than ${formatOperand(comparator.gt)}`);
    if (comparator.gte !== undefined) phrases.push(`is at least ${formatOperand(comparator.gte)}`);
    if (comparator.lt !== undefined) phrases.push(`is less than ${formatOperand(comparator.lt)}`);
    if (comparator.lte !== undefined) phrases.push(`is at most ${formatOperand(comparator.lte)}`);
  }
  if (comparator.equals !== undefined) phrases.push(`is ${formatOperand(comparator.equals)}`);
  if (comparator.contains !== undefined) phrases.push(`contains ${formatOperand(comparator.contains)}`);
  if (comparator.startsWith !== undefined) phrases.push(`starts with ${formatOperand(comparator.startsWith)}`);
  if (comparator.endsWith !== undefined) phrases.push(`ends with ${formatOperand(comparator.endsWith)}`);
  if (comparator.in !== undefined) phrases.push(`is one of ${formatList(comparator.in, 'or')}`);
  if (comparator.notIn !== undefined) phrases.push(`is not one of ${formatList(comparator.notIn, 'or')}`);
  if (comparator.not !== undefined && keys.length > 1) phrases.push(describeNot(comparator.not));
  for (const key of keys) {
    if (COMPARATOR_KEYS.has(key)) continue;
    phrases.push(`${humanize(key)} ${formatOperand(comparator[key])}`);
  }
  if (phrases.length === 0) return null;
  return joinList(phrases, 'and');
}

function describeNot(value: unknown): string {
  if (isPlainObject(value) && isComparator(value)) {
    const inner = describeComparator(value);
    return inner ? negatePhrase(inner) : 'is not empty';
  }
  return `is not ${formatOperand(value)}`;
}

function negatePhrase(phrase: string): string {
  if (phrase.startsWith('is one of ')) return `is not one of ${phrase.slice('is one of '.length)}`;
  if (phrase.startsWith('is not ')) return phrase;
  if (phrase.startsWith('is ')) return `is not ${phrase.slice('is '.length)}`;
  if (phrase.startsWith('contains ')) return `does not contain ${phrase.slice('contains '.length)}`;
  if (phrase.startsWith('starts with ')) return `does not start with ${phrase.slice('starts with '.length)}`;
  if (phrase.startsWith('ends with ')) return `does not end with ${phrase.slice('ends with '.length)}`;
  return `is not (${phrase})`;
}

function describeGroupedBy(by: unknown, labels?: ReadonlyMap<string, string>): string | null {
  if (!Array.isArray(by)) return null;
  const fields = by
    .filter((field): field is string => typeof field === 'string' && field.trim() !== '')
    .map((field) => plainFieldLabel(field.trim(), labels));
  if (fields.length === 0) return null;
  return `Grouped by ${joinList(fields, 'and')}`;
}

function describeHaving(having: unknown, labels?: ReadonlyMap<string, string>): string | null {
  if (!isPlainObject(having) || Object.keys(having).length === 0) return null;
  if (isComparator(having)) {
    const phrase = describeComparator(having);
    return phrase ? `With ${stripLeadingIs(phrase)}` : null;
  }
  const clause = describeWhere(having, labels);
  return clause ? `With ${clause}` : null;
}

function describeOrderBy(orderBy: unknown, labels?: ReadonlyMap<string, string>): string | null {
  if (orderBy == null || orderBy === '') return null;
  if (typeof orderBy === 'string') {
    const label = plainFieldLabel(orderBy.trim(), labels);
    return label ? `Sorted by ${label}` : null;
  }
  const items = Array.isArray(orderBy) ? orderBy : [orderBy];
  const parts: string[] = [];
  for (const item of items) {
    if (!isPlainObject(item)) {
      if (item != null && item !== '') parts.push(formatOperand(item));
      continue;
    }
    for (const [field, direction] of Object.entries(item)) {
      parts.push(describeSortField(field, direction, labels));
    }
  }
  if (parts.length === 0) return null;
  return `Sorted by ${joinList(parts, 'and', { commaBeforePair: true })}`;
}

function describeSortField(
  field: string,
  direction: unknown,
  labels: ReadonlyMap<string, string> | undefined,
): string {
  const label = plainFieldLabel(field, labels);
  if (isPlainObject(direction) && !('sort' in direction)) {
    const nested = Object.entries(direction).map(([key, value]) => describeSortField(key, value, labels));
    if (nested.length > 0) return `${label} ${joinList(nested, 'and')}`;
  }
  const word = directionWord(direction);
  return word ? `${label}, ${word}` : label;
}

function directionWord(direction: unknown): string | null {
  if (typeof direction === 'string') {
    const normalized = direction.trim().toLowerCase();
    if (normalized === 'asc') return 'ascending';
    if (normalized === 'desc') return 'descending';
    if (!normalized) return null;
    return humanize(normalized);
  }
  if (!isPlainObject(direction)) return null;
  const sort = directionWord(direction.sort);
  const nulls = typeof direction.nulls === 'string' && direction.nulls.trim() ? humanize(direction.nulls) : null;
  if (sort && nulls) return `${sort}, ${nulls}`;
  return sort;
}

function describeLoose(value: unknown, labels?: ReadonlyMap<string, string>): string {
  if (value == null) return 'empty';
  if (typeof value !== 'object') return formatOperand(value);
  if (Array.isArray(value)) {
    const parts = value.map((item) => describeLoose(item, labels)).filter(Boolean);
    return parts.join(', ');
  }
  return Object.entries(value)
    .map(([key, item]) => {
      const label = plainFieldLabel(key, labels);
      const rest = describeLoose(item, labels);
      return rest ? `${label} ${rest}` : label;
    })
    .join(', ');
}

function stripLeadingIs(phrase: string): string {
  return phrase.startsWith('is ') ? phrase.slice('is '.length) : phrase;
}

function plainFieldLabel(field: string, labels?: ReadonlyMap<string, string>): string {
  const curated = labels?.get(field)?.trim();
  if (curated) return curated;
  const normalized = /[a-z]/.test(field) ? field : field.toLowerCase();
  return humanize(normalized);
}

function isComparator(value: unknown): value is Record<string, unknown> {
  if (!isPlainObject(value)) return false;
  const keys = Object.keys(value).filter((key) => key !== 'mode');
  return keys.length > 0 && keys.every((key) => COMPARATOR_KEYS.has(key));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function formatList(value: unknown, conjunction: 'and' | 'or'): string {
  const items = Array.isArray(value) ? value : [value];
  return joinList(items.map((item) => formatOperand(item)), conjunction);
}

function formatOperand(value: unknown): string {
  if (value == null) return 'empty';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'string') {
    const dated = formatUtcDateTime(value);
    if (dated) return dated;
    return `"${value.replace(/"/g, '\\"')}"`;
  }
  if (Array.isArray(value)) return value.map((item) => formatOperand(item)).join(', ');
  return describeLoose(value);
}

/** UTC clock time so a citation summary does not depend on the machine timezone. */
function formatUtcDateTime(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const month = UTC_MONTHS[date.getUTCMonth()];
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');
  const hours24 = date.getUTCHours();
  const hours12 = hours24 % 12 || 12;
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  return `${month} ${day}, ${year}, ${hours12}:${minutes} ${ampm} UTC`;
}

function joinList(
  parts: string[],
  conjunction: 'and' | 'or',
  options?: { commaBeforePair?: boolean },
): string {
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0] ?? '';
  if (parts.length === 2) {
    return options?.commaBeforePair
      ? `${parts[0]}, ${conjunction} ${parts[1]}`
      : `${parts[0]} ${conjunction} ${parts[1]}`;
  }
  return `${parts.slice(0, -1).join(', ')}, ${conjunction} ${parts[parts.length - 1]}`;
}

/** Flatten a groupBy hydrate row so key fields and rollups are table columns. */
export function flattenGroupRow(row: Record<string, unknown>): Record<string, unknown> {
  const nested =
    row.key && typeof row.key === 'object' && !Array.isArray(row.key)
      ? (row.key as Record<string, unknown>)
      : {};
  const flat: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(row)) {
    if (field === 'key') continue;
    if (
      (field === 'min' || field === 'max' || field === 'sum' || field === 'avg') &&
      value &&
      typeof value === 'object' &&
      !Array.isArray(value)
    ) {
      for (const [name, item] of Object.entries(value as Record<string, unknown>)) {
        if (item != null) flat[`${field}.${name}`] = item;
      }
      continue;
    }
    flat[field] = value;
  }
  Object.assign(flat, nested);
  return flat;
}

/** Column fields for flattened groupBy hydrate rows, key fields then count then rollups. */
export function citationGroupColumns(rows: Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  const keys: string[] = [];
  const rollups: string[] = [];
  for (const row of rows) {
    for (const field of Object.keys(flattenGroupRow(row))) {
      if (seen.has(field)) continue;
      seen.add(field);
      if (field.includes('.')) rollups.push(field);
      else if (field !== 'count') keys.push(field);
    }
  }
  const columns = [...keys];
  if (seen.has('count')) columns.push('count');
  columns.push(...rollups);
  return columns;
}

export function formatGroupCell(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function humanizeGroupField(field: string): string {
  const spaced = field.replace(/\./g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.replace(/\b\w/g, (character) => character.toUpperCase());
}

export function sortCitationRows(
  rows: Record<string, unknown>[],
  sort: { field: string; direction: 'asc' | 'desc' } | null,
): Record<string, unknown>[] {
  if (!sort) return rows;
  const { field, direction } = sort;
  const mul = direction === 'asc' ? 1 : -1;
  return [...rows].sort((left, right) => compareCell(left[field], right[field]) * mul);
}

export function filterCitationRows(
  rows: Record<string, unknown>[],
  filters: FilterState,
): Record<string, unknown>[] {
  const active = Object.entries(filters).filter(([, value]) => isFilterActive(value));
  if (active.length === 0) return rows;
  return rows.filter((row) => active.every(([field, value]) => matchFilter(row[field], value)));
}

function compareCell(left: unknown, right: unknown): number {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), undefined, { numeric: true });
}

function matchFilter(cell: unknown, value: ColumnFilterValue): boolean {
  const text = cell == null ? '' : String(cell);
  switch (value.kind) {
    case 'string': {
      const needle = value.value.trim().toLowerCase();
      if (!needle) return true;
      const hay = text.toLowerCase();
      if (value.op === 'equals') return hay === needle;
      if (value.op === 'startsWith') return hay.startsWith(needle);
      if (value.op === 'endsWith') return hay.endsWith(needle);
      return hay.includes(needle);
    }
    case 'number': {
      const n = Number(value.value);
      const cellN = typeof cell === 'number' ? cell : Number(cell);
      if (!Number.isFinite(n) || !Number.isFinite(cellN)) return false;
      if (value.op === 'gt') return cellN > n;
      if (value.op === 'gte') return cellN >= n;
      if (value.op === 'lt') return cellN < n;
      if (value.op === 'lte') return cellN <= n;
      return cellN === n;
    }
    case 'boolean':
      return Boolean(cell) === value.value;
    case 'enum':
      return value.values.includes(text);
    case 'datetime': {
      const t = Date.parse(text);
      if (Number.isNaN(t)) return false;
      if (value.gte && t < Date.parse(value.gte)) return false;
      if (value.lte && t > Date.parse(value.lte)) return false;
      return true;
    }
  }
}
